import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useActivarRol, useRolesAsignados } from '@/hooks/useCambiarRolActivo';
import { ListaOpcionesRol } from './ListaOpcionesRol';
import { opcionDeRol, type OpcionRol } from './opcionesRol';

interface CambiarRolDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "Cambiar de rol" para el menú de usuario del admin: el mismo selector del
 * login, con los roles elegibles de `user_roles` (sin 23/36/43).
 * Mostrar la opción solo si `useRolesAsignados().elegiblesAdmin.length > 1`.
 */
export function CambiarRolDialog({ open, onOpenChange }: CambiarRolDialogProps) {
  const { profile } = useAuth();
  const { elegiblesAdmin } = useRolesAsignados();
  const activarRol = useActivarRol();
  const navigate = useNavigate();
  const [seleccionando, setSeleccionando] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const opciones = elegiblesAdmin.map((r) => opcionDeRol(r, profile?.rol_id));

  const handleSelect = async (opcion: OpcionRol) => {
    setError(null);
    if (opcion.viaRpc && opcion.rolId === profile?.rol_id) {
      onOpenChange(false);
      return;
    }
    setSeleccionando(opcion.key);
    try {
      if (opcion.viaRpc) {
        const resultado = await activarRol(opcion.rolId);
        if (resultado.estado === 'sin_rpc') {
          setError('El cambio de rol todavía no está disponible en este ambiente.');
          return;
        }
        if (resultado.estado !== 'ok') {
          setError(resultado.mensaje);
          return;
        }
      }
      onOpenChange(false);
      if (opcion.externa) {
        window.location.assign(opcion.route);
        return;
      }
      navigate(opcion.route, { replace: true });
    } finally {
      setSeleccionando(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (seleccionando ? undefined : onOpenChange(v))}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cambiar de rol</DialogTitle>
          <DialogDescription>Selecciona con qué rol quieres trabajar.</DialogDescription>
        </DialogHeader>
        {error && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm" style={{ color: 'hsl(0 84% 40%)', background: 'hsl(0 84% 97%)' }}>
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <ListaOpcionesRol opciones={opciones} onSelect={handleSelect} seleccionando={seleccionando} />
      </DialogContent>
    </Dialog>
  );
}
